import {
  type SecureInputsElements,
  type SecureInputsElementsForTokenization,
  type SecureInputsElementsForCardNumberOnly,
  type SecureInputsTokenizationConfig,
  SecureInputsGlobalPostMessageEventKind,
  secureInputFields,
  type Maybe,
  type SecureInputsConfig,
  type SecureInputsFields,
  type SecureInputsPostMessageEvent,
  type SecureInputsRenderedFields,
  type SecureInputsSubmitError,
  type ClientToken,
  type HighnoteEnvironment,
  type SecureInputsTokenizationSubmitArguments,
  type SecureInputsTokenizationRenderedFields,
  type SecureInputsCardBrand,
  type SecureInputsError,
  type SecureInputsTokenizationError,
  type SecureInputsValidityState,
  type PaymentMethodToken,
} from "@highnoteplatform/types";

export type {
  SecureInputsError,
  PaymentMethodToken,
  SecureInputsTokenizationError,
  SecureInputsValidityState,
  SecureInputsTokenizationRenderedFields,
  SecureInputsTokenizationSubmitArguments,
  SecureInputsTokenizationConfig,
  SecureInputsFields,
};

import { buildFrame } from "./buildFrame";
import { iframeOrigin, generateUuid } from "./config";
import { handlePostMessage } from "./handlePostMessage";

interface Field {
  container: Element;
  frame: HTMLIFrameElement;
}

type FieldsMap = Readonly<Record<SecureInputsFields, Maybe<Field>>>;

const unmount = (fields: Readonly<FieldsMap>) => {
  Object.values(fields).forEach((field) => {
    if (field) {
      field.container.replaceChildren("");
    }
  });
};

const submit = (
  fieldName: keyof FieldsMap,
  field: Maybe<Field>,
  event: SecureInputsPostMessageEvent
): Maybe<SecureInputsSubmitError> => {
  const targetWindow = field?.frame.contentWindow;

  if (targetWindow === null || targetWindow === undefined) {
    return {
      name: "SecureInputsSubmitError",
      message: `Unable to submit field: ${fieldName}`,
    };
  }

  targetWindow.postMessage(event, iframeOrigin);

  return undefined;
};

interface GenerateFieldsArguments {
  uuid: string;
  elements:
    | SecureInputsElements
    | SecureInputsElementsForCardNumberOnly
    | SecureInputsElementsForTokenization;
  clientToken: ClientToken;
  environment: HighnoteEnvironment;
  excludedCardBrands?: SecureInputsCardBrand[];
}

const generateFields = ({
  uuid,
  elements,
  clientToken,
  environment,
  excludedCardBrands,
}: Readonly<GenerateFieldsArguments>): FieldsMap => {
  const fields: SecureInputsFields[] = Object.keys(elements).map(
    (element) => element as SecureInputsFields
  );
  const fieldMap: FieldsMap = secureInputFields.reduce<FieldsMap>(
    (accum, field): FieldsMap => {
      const elementConfig = (elements as Record<string, unknown>)[field];

      if (
        !elementConfig ||
        typeof elementConfig !== "object" ||
        !("selector" in elementConfig)
      ) {
        return accum;
      }

      const config = elementConfig as { selector: string };

      const frame = buildFrame({
        clientToken,
        uuid,
        field,
        environment,
        fields,
        excludedCardBrands,

        elementConfig: {
          [field]: elementConfig,
        },
      });

      const container =
        document.querySelector(config.selector) ?? document.body;

      container.append(frame);

      return {
        ...accum,

        [field]: {
          frame,
          container,
        },
      };
    },
    {
      pin: undefined,
      cardNumber: undefined,
      expirationYear: undefined,
      expirationDate: undefined,
      expirationMonth: undefined,
      securityCode: undefined,
    }
  );

  return fieldMap;
};

const renderTokenizationFields = async ({
  onStateChange,
  onSuccess,
  onError,
  onReady,
  elements,
  clientToken,
  environment = "test",
  excludedCardBrands = [],
}: Readonly<SecureInputsTokenizationConfig>): Promise<SecureInputsTokenizationRenderedFields> => {
  const uuid = generateUuid();
  const fields = generateFields({
    uuid,
    elements,
    clientToken,
    environment,
    excludedCardBrands,
  });

  window.removeEventListener(
    "message",
    handlePostMessage({
      uuid,
      onTokenizeCardError: onError,
      onTokenizeCardSuccess: onSuccess,
      onStateChange,
      onReady,
    })
  );
  window.addEventListener(
    "message",
    handlePostMessage({
      uuid,
      onTokenizeCardError: onError,
      onTokenizeCardSuccess: onSuccess,
      onStateChange,
      onReady,
    })
  );

  return {
    submit: (
      args?: SecureInputsTokenizationSubmitArguments
    ): Maybe<SecureInputsSubmitError> => {
      const { additionalData } = args ?? {};

      return submit("cardNumber", fields.cardNumber, {
        __typename: SecureInputsGlobalPostMessageEventKind.TOKENIZE_CARD,
        additionalData,
      });
    },

    unmount: async () => {
      unmount(fields);
    },
  };
};

const renderFieldsForPinEntry = async ({
  onSuccess,
  onInput,
  onError,
  elements,
  clientToken,
  environment = "test",
}: Readonly<SecureInputsConfig>): Promise<SecureInputsRenderedFields> => {
  const uuid = generateUuid();
  const fields = generateFields({
    uuid,
    elements,
    clientToken: clientToken ?? elements.pin.clientToken ?? "",
    environment,
  });

  window.removeEventListener(
    "message",
    handlePostMessage({
      uuid,
      onInput,
      onPinEntryError: onError,
      onPinEntrySuccess: onSuccess,
    })
  );
  window.addEventListener(
    "message",
    handlePostMessage({
      uuid,
      onInput,
      onPinEntryError: onError,
      onPinEntrySuccess: onSuccess,
    })
  );

  return {
    submit: (): Maybe<SecureInputsSubmitError> =>
      submit("pin", fields.pin, {
        __typename: SecureInputsGlobalPostMessageEventKind.CHANGE_CARD_PIN,
      }),

    unmount: async () => {
      unmount(fields);
    },
  };
};

const isClientTokenDefined = (
  config: SecureInputsConfig | SecureInputsTokenizationConfig
): boolean =>
  config.clientToken !== undefined ||
  ("pin" in config.elements && config.elements.pin.clientToken !== undefined);

export const renderFieldsForTokenization = async (
  config: SecureInputsTokenizationConfig
): Promise<SecureInputsTokenizationRenderedFields> => {
  if (!isClientTokenDefined(config)) {
    throw new Error("clientToken was undefined");
  }

  return await renderTokenizationFields(config);
};

export const renderFields = async (
  config: SecureInputsConfig
): Promise<SecureInputsRenderedFields> => {
  if (!isClientTokenDefined(config)) {
    throw new Error("clientToken was undefined");
  }

  return await renderFieldsForPinEntry(config);
};
